/**
 * Os Funis e as Etapas de cada um (FR-1, FR-2, FR-68) — a tela "Gerenciar funis e etapas"
 * (`/funil/gerenciar`, 11.2), redesenhada na 11.4:
 *
 * - **o Funil novo nasce com as Etapas dele, de uma vez** — nome do Funil e uma lista de linhas
 *   (nome e cor de cada Etapa), num `POST` só; o serviço cria tudo numa transação;
 * - **cada Etapa tem cor** da paleta pastel fechada, ou nenhuma (sem seletor livre); a cor pinta a
 *   coluna do quadro, e aqui ela aparece como amostra **e** nome por extenso;
 * - renomear e mudar a cor de uma Etapa é o mesmo formulário (o `PUT` leva os dois);
 * - não há "caixinhas por funil" (DW-305: acréscimo órfão do protótipo).
 *
 * **Excluir é em dois passos, e às vezes não é possível.** "Excluir" abre "Confirmar
 * exclusão"/"Cancelar" na própria linha. Com Negócio dentro, a ação nem chega ao primeiro
 * passo: ela aparece como **"Bloqueado"**, desabilitada, com a razão ao lado — o FR-68 recusa
 * a exclusão enquanto houver Negócio, e **nenhum Negócio é movido** para abrir caminho.
 * Desabilitar aqui não é a autorização: quem recusa continua sendo o serviço, e a mensagem
 * dele vai à tela verbatim — o botão desabilitado só evita a ida à rede de um pedido que já se
 * sabe recusado.
 *
 * **Reordenar é por botões, e não por arrastar.** Subir e descer trocam a Etapa com a vizinha
 * e enviam a **lista completa** ao serviço, que reescreve as posições numa transação. Não há
 * biblioteca de arrastar-e-soltar: ela custaria uma dependência e um gesto que não existe no
 * teclado nem no leitor de tela, para um caso de três a seis colunas.
 *
 * **Esconder não é autorizar, e continua não sendo.** A tela mostra criar, renomear, reordenar
 * e excluir sem consultar o perfil de quem entrou; quem recusa é o serviço, e a mensagem dele
 * vai à tela verbatim. É a convenção desde a 2.1.
 *
 * **Cada cartão mostra o tipo do Funil, e só mostra (3.7).** Desde a 13.11a os dois tipos têm o
 * mesmo piso — o Responsável e a gestão (FR-31 emendado) —, e quem recorta é a RLS do serviço,
 * linha a linha; o tipo continua decidindo as métricas da aba Prospecção dos Relatórios
 * (DW-489). O rótulo existe porque esta é a **única** tela em que um humano
 * observa esse piso — e ele não vira campo de formulário: nenhuma rota aceita `tipo` em corpo, e
 * o Funil que a gestão cria nasce restrito.
 *
 * **Depois de cada mutação o catálogo é relido do serviço**, e não remendado com a resposta: a
 * mesma chave de cache alimenta o seletor de Funis, e a resposta de uma mutação é um Funil (ou
 * uma Etapa), não a lista com as contagens recalculadas.
 *
 * **Paridade de celular (NFR-1).** Não há função exclusiva do desktop: o que muda é a grade —
 * dois Funis lado a lado no computador, empilhados no celular —, e a decisão é do
 * `useEhCelular` do shell, por `matchMedia`. Sem `@media`.
 */

import { type FormEvent, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useEhCelular } from '@/shell/Shell'
import {
  CORES_PASTEL,
  type CorPastel,
  type Etapa,
  type Funil,
  NOMES_DAS_CORES,
  useAtualizarEtapa,
  useCriarEtapa,
  useCriarFunil,
  useExcluirEtapa,
  useExcluirFunil,
  useFunis,
  useRenomearFunil,
  useReordenarEtapas,
} from './api'
import './funis-e-etapas.css'

/**
 * O teto de coluna do serviço (`servicos/negocios/dominio/funil.py`), pela mesma regra do
 * formulário da ficha: o servidor continua sendo quem decide, e o `maxLength` só evita gastar
 * uma ida à rede com o que o cliente já sabe.
 */
const TAMANHO_DO_NOME = 120

const FALTA_NOME = 'Informe o nome.'
const FALTA_NOME_DE_ETAPA = 'Informe o nome de cada etapa.'
export const ETAPAS_REPETIDAS = 'Duas etapas do funil novo têm o mesmo nome.'

/** O teto de Etapas do Funil novo — o `max_length` de `CorpoDeNovoFunil.etapas` no serviço. */
export const MAXIMO_DE_ETAPAS_NO_FUNIL_NOVO = 30
const SEM_COR = 'Nenhuma'

/** Confirmações. Sem elas a pessoa reenvia por não saber se gravou. */
const FUNIL_CRIADO = 'Funil criado.'
const FUNIL_SALVO = 'Funil salvo.'
const FUNIL_EXCLUIDO = 'Funil excluído.'
const ETAPA_CRIADA = 'Etapa criada.'
const ETAPA_SALVA = 'Etapa salva.'
const ETAPA_EXCLUIDA = 'Etapa excluída.'
const ORDEM_SALVA = 'Ordem das etapas salva.'

const CARREGANDO = 'Carregando os funis…'
const SEM_FUNIS = 'Nenhum funil cadastrado. Crie o primeiro abaixo.'
const SEM_ETAPAS = 'Este funil ainda não tem etapas.'

/**
 * As razões de "Bloqueado" — e são **duas**, porque o que desbloqueia cada uma é diferente.
 *
 * Elas são a metade da tela que o FR-68 exige: um botão desabilitado sem explicação é uma
 * parede. A recusa do **serviço** continua sendo a que aparece quando alguém chega lá por
 * outro caminho — estas são só a antecipação dela, e por isso precisam dizer a verdade.
 *
 * **A instrução da Etapa não serve para o Funil.** A Etapa destrava quando os Negócios dela
 * vão para outra Etapa — inclusive uma do mesmo Funil. O Funil está bloqueado pela **soma** de
 * todas as Etapas dele, então mover os Negócios de uma Etapa para a vizinha não desbloqueia
 * nada: eles têm de sair do Funil. Um texto só, compartilhado, mandaria quem quer excluir o
 * Funil fazer um trabalho que não muda a resposta do serviço.
 */
const BLOQUEIO_DA_ETAPA = 'Mova os negócios para outra etapa primeiro.'

const BLOQUEIO_DO_FUNIL = 'Mova os negócios deste funil para outro funil primeiro.'

/** O nome acessível da seção — é ele que faz a `<section>` virar uma `region`. */
const ID_DO_TITULO = 'funis-titulo'

/** `id` do campo do formulário de novo funil — a recusa local dele deriva deste. */
const ID_DO_CAMPO_DO_NOVO_FUNIL = 'funil-nome-novo'

/** O nome da cor por extenso, ou "Nenhuma" — a cor nunca é o único sinal. */
function nomeDaCor(cor: CorPastel | null): string {
  return cor === null ? SEM_COR : NOMES_DAS_CORES[cor]
}

/** A mensagem do serviço quando ela existe; a genérica quando a falha nem chegou a ter uma. */
function mensagemDe(falha: unknown): string {
  return falha instanceof ErroDaApi ? falha.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

/**
 * O rótulo do tipo de cada Funil — o **único** lugar onde um humano observa o piso (3.7, FR-31).
 *
 * Ele diz o que o tipo **significa**, e não como o tipo se chama. O nome do tipo não entra: ao
 * lado do nome do Funil ele sairia como "Prospecção · Prospecção" numa instalação de fábrica, e
 * como "Captação · Prospecção" num Funil renomeado — que é exatamente a confusão entre rótulo e
 * piso que a coluna existe para desfazer. Quem precisa do valor cru é a suíte, e ela o alcança
 * pela classe `funis__tipo--<tipo>`.
 *
 * **Desde a 13.11c os dois tipos têm o mesmo piso** (FR-31 emendado, decisão de 22/10/2026): a
 * Prospecção fechou, e "é responsável, vê" vale em todo Funil. O tipo continua existindo — ele separa
 * as métricas da aba Prospecção —, mas não muda mais quem enxerga o Negócio.
 *
 * **A tela não escreve o tipo, e é por isso que ele é rótulo e não seletor.** Nenhuma rota o
 * aceita em corpo (`POST /funis` e `PUT /funis/{id}` respondem `422`): o Funil que a gestão cria
 * nasce restrito, e abrir um Funil é ato que ainda não tem história que o descreva. Um seletor
 * aqui prometeria uma escrita que o serviço recusa.
 */
const ROTULO_DO_TIPO: Record<Funil['tipo'], string> = {
  prospeccao: 'restrito ao responsável e à gestão',
  vendas: 'restrito ao responsável e à gestão',
}

/**
 * O que o rótulo diz quando o tipo não é um dos dois conhecidos.
 *
 * `funil.py` anuncia que um terceiro valor é um `ALTER TABLE` de CHECK, e não uma cirurgia de
 * tipo — ou seja, é barato, e vai acontecer antes de esta tela saber. Sem saída, o acesso ao
 * mapa devolveria `undefined` e a tela **inteira** de Administração quebraria num `TypeError`:
 * um serviço à frente do SPA (ou uma resposta sem o campo) derrubaria os Funis, as Etapas e a
 * régua junto. Degradar para "restrição desconhecida" custa uma linha e mantém tudo de pé —
 * e não mente: quem lê fica sabendo que há um piso, e que esta tela não sabe qual.
 */
const TIPO_DESCONHECIDO = 'restrição desconhecida'

/**
 * `true` quando o tipo é um dos que esta tela sabe explicar.
 *
 * Ele existe por duas razões, e nenhuma é estilo. A primeira é que um acesso cru ao mapa
 * alcança as chaves de `Object.prototype`: `ROTULO_DO_TIPO['constructor']` devolveria uma
 * função, e o `??` não a pegaria — o rótulo sairia como algo que não é texto. A segunda é o
 * tipo do mapa: escrito sobre `Funil['tipo']`, o dia em que o serviço ganhar um terceiro valor
 * o `npm run typecheck` **reprova** até alguém escrever o que ele significa, em vez de a tela
 * degradar em silêncio para "restrição desconhecida" numa instalação de verdade. A saída
 * continua lá para o serviço que já estiver à frente deste SPA.
 */
function ehTipoConhecido(tipo: string | undefined): tipo is Funil['tipo'] {
  return tipo !== undefined && Object.hasOwn(ROTULO_DO_TIPO, tipo)
}

function explicacaoDoTipo(tipo: string | undefined): string {
  return ehTipoConhecido(tipo) ? ROTULO_DO_TIPO[tipo] : TIPO_DESCONHECIDO
}

/** "1 negócio" / "n negócios" / "sem negócios" — o subtítulo de cada Funil e de cada Etapa. */
function contagem(quantidade: number): string {
  if (quantidade === 0) {
    return 'sem negócios'
  }
  return quantidade === 1 ? '1 negócio' : `${String(quantidade)} negócios`
}

function CampoDeNome({
  id,
  rotulo,
  valor,
  aoTrocar,
  recusadoPor,
}: {
  id: string
  rotulo: string
  valor: string
  aoTrocar: (valor: string) => void
  /**
   * `id` da mensagem que recusou **este** campo, quando há uma.
   *
   * É o que liga a recusa ao campo que ela nomeia: sem `aria-invalid` e `aria-describedby`,
   * quem usa leitor de tela ouve "Informe o nome" solto e precisa adivinhar de qual dos
   * formulários da tela a mensagem fala.
   */
  recusadoPor?: string | null
}) {
  return (
    <div className="funis__campo">
      <label className="funis__rotulo" htmlFor={id}>
        {rotulo}
      </label>
      <input
        id={id}
        className="funis__entrada"
        type="text"
        value={valor}
        maxLength={TAMANHO_DO_NOME}
        aria-invalid={recusadoPor ? true : undefined}
        aria-describedby={recusadoPor ?? undefined}
        onChange={(evento) => {
          aoTrocar(evento.target.value)
        }}
      />
    </div>
  )
}

/**
 * O par de ações de exclusão de uma linha: dois passos, ou "Bloqueado" quando há Negócio.
 *
 * Ele é um componente porque a regra é a **mesma** para o Funil e para a Etapa — o FR-68 não
 * distingue os dois —, e duas cópias divergiriam no primeiro ajuste, com o sintoma "a etapa
 * bloqueia e o funil deixa passar".
 */
function AcaoDeExcluir({
  id,
  alvo,
  razaoDoBloqueio,
  quantidadeDeNegocios,
  aConfirmar,
  ocupado,
  aoPedir,
  aoCancelar,
  aoExcluir,
}: {
  /** Prefixo dos `id` deste par de ações — é dele que sai o `id` da razão do bloqueio. */
  id: string
  /** O que some, por extenso — vai no `aria-label`, porque os botões são muitos e iguais. */
  alvo: string
  /** O que destrava **este** alvo. Funil e Etapa não destravam do mesmo jeito. */
  razaoDoBloqueio: string
  quantidadeDeNegocios: number
  aConfirmar: boolean
  ocupado: boolean
  aoPedir: () => void
  aoCancelar: () => void
  aoExcluir: () => void
}) {
  if (quantidadeDeNegocios > 0) {
    const idDaRazao = `${id}-bloqueio`
    return (
      <>
        {/*
         * O `aria-describedby` é o que liga o botão à razão — o mesmo par que `CampoDeRotulo`
         * usa para ligar um campo à recusa dele. Sem ele a frase ao lado é texto solto, e quem
         * navega por controles ouve "Bloqueado" sem nunca saber o que destrava.
         *
         * O `disabled` fica: o clique tem de ser impossível (o serviço recusaria de qualquer
         * jeito, e gastar a ida à rede para ouvir um `409` previsível é ruído), e é ele o gancho
         * do `:disabled` no CSS. A descrição continua alcançável pelo cursor virtual do leitor
         * de tela, que é como se lê conteúdo estático.
         */}
        <button
          className="funis__botao funis__botao--discreto funis__botao--mudo"
          type="button"
          disabled
          title={razaoDoBloqueio}
          aria-label={`Excluir ${alvo} — bloqueado`}
          aria-describedby={idDaRazao}
        >
          Bloqueado
        </button>
        {/* A razão fica no `title` e para o leitor de tela (`aria-describedby`); escrita em toda
            linha ela virava ruído (protótipo v2: a dica é uma só, no rodapé da tela). */}
        <span className="funis__ajuda funis__so-para-leitor" id={idDaRazao}>
          {razaoDoBloqueio}
        </span>
      </>
    )
  }

  if (aConfirmar) {
    return (
      <>
        {/*
         * O segundo passo. O nome do alvo vai no `aria-label` porque numa tela com vários
         * funis e várias etapas um "Confirmar exclusão" solto não diz **qual** some — e o que
         * some leva as etapas dele junto, por cascata.
         */}
        <button
          className="funis__botao funis__botao--perigo"
          type="button"
          disabled={ocupado}
          aria-label={`Confirmar a exclusão de ${alvo}`}
          onClick={aoExcluir}
        >
          Confirmar exclusão
        </button>
        <button
          className="funis__botao funis__botao--discreto"
          type="button"
          disabled={ocupado}
          aria-label={`Cancelar a exclusão de ${alvo}`}
          onClick={aoCancelar}
        >
          Cancelar
        </button>
      </>
    )
  }

  return (
    <button
      className="funis__botao funis__botao--discreto"
      type="button"
      disabled={ocupado}
      aria-label={`Excluir ${alvo}`}
      onClick={aoPedir}
    >
      Excluir
    </button>
  )
}

/** Um formulário de um campo só — o de renomear em linha e o de criar. */
function FormularioDeNome({
  id,
  rotulo,
  inicial,
  acao,
  cancelar,
  ocupado,
  enviando,
  aoEnviar,
  aoCancelar,
}: {
  id: string
  rotulo: string
  inicial: string
  /** O texto do botão de envio, e o que ele diz enquanto grava. */
  acao: { parado: string; gravando: string; nomeAcessivel: string }
  /** O rótulo acessível do "Cancelar", quando este formulário tem um. */
  cancelar?: string
  ocupado: boolean
  enviando: boolean
  aoEnviar: (nome: string) => void
  aoCancelar?: () => void
}) {
  const [rascunho, setRascunho] = useState(inicial)
  const [recusa, setRecusa] = useState<string | null>(null)
  const idDaRecusa = `${id}-recusa`

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (rascunho.trim() === '') {
      setRecusa(FALTA_NOME)
      return
    }
    aoEnviar(rascunho.trim())
  }

  return (
    <form className="funis__formulario" onSubmit={enviar} noValidate>
      <CampoDeNome
        id={id}
        rotulo={rotulo}
        valor={rascunho}
        recusadoPor={recusa === null ? null : idDaRecusa}
        aoTrocar={(valor) => {
          setRecusa(null)
          setRascunho(valor)
        }}
      />
      {recusa === null ? null : (
        <p className="funis__erro" id={idDaRecusa} role="alert">
          {recusa}
        </p>
      )}
      <div className="funis__acoes">
        {/* O rótulo segue **este** envio, e o `disabled` segue a tela inteira: outra gravação
            ocupa a tela, mas não é esta que está gravando — e um botão que diz "Salvando…" sem
            estar salvando mente para o leitor de tela. */}
        <button
          className="funis__botao"
          type="submit"
          disabled={ocupado}
          aria-label={acao.nomeAcessivel}
        >
          {enviando ? acao.gravando : acao.parado}
        </button>
        {aoCancelar === undefined ? null : (
          <button
            className="funis__botao funis__botao--discreto"
            type="button"
            disabled={ocupado}
            aria-label={cancelar}
            onClick={() => {
              setRascunho(inicial)
              setRecusa(null)
              aoCancelar()
            }}
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  )
}

/**
 * A cor de uma Etapa (11.4): a paleta pastel fechada do `DESIGN.md`, ou nenhuma. Sem seletor
 * livre. A amostra ao lado é decoração — o nome da cor está escrito no próprio seletor.
 */
function SeletorDeCor({
  id,
  rotulo,
  valor,
  desabilitado,
  aoTrocar,
}: {
  id: string
  rotulo: string
  valor: CorPastel | null
  desabilitado?: boolean
  aoTrocar: (cor: CorPastel | null) => void
}) {
  return (
    <div className="funis__campo funis__campo--cor">
      <label className="funis__rotulo" htmlFor={id}>
        {rotulo}
      </label>
      <span className="funis__cor">
        <span className="funis__amostra" data-cor={valor ?? 'nenhuma'} aria-hidden="true" />
        <select
          id={id}
          className="funis__entrada funis__entrada--cor"
          value={valor ?? ''}
          disabled={desabilitado}
          onChange={(evento) => {
            const escolhida = evento.target.value
            aoTrocar(escolhida === '' ? null : (escolhida as CorPastel))
          }}
        >
          <option value="">{SEM_COR}</option>
          {CORES_PASTEL.map((cor) => (
            <option key={cor} value={cor}>
              {NOMES_DAS_CORES[cor]}
            </option>
          ))}
        </select>
      </span>
    </div>
  )
}

/** O formulário de uma Etapa: nome e cor — para criar no fim do Funil e para editar (11.4). */
function FormularioDaEtapa({
  id,
  inicial,
  acao,
  cancelar,
  ocupado,
  enviando,
  aoEnviar,
  aoCancelar,
}: {
  id: string
  inicial: { nome: string; cor: CorPastel | null }
  acao: { parado: string; gravando: string; nomeAcessivel: string }
  cancelar: string
  ocupado: boolean
  enviando: boolean
  aoEnviar: (etapa: { nome: string; cor: CorPastel | null }) => void
  aoCancelar: () => void
}) {
  const [nome, setNome] = useState(inicial.nome)
  const [cor, setCor] = useState<CorPastel | null>(inicial.cor)
  const [recusa, setRecusa] = useState<string | null>(null)
  const idDaRecusa = `${id}-recusa`

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (nome.trim() === '') {
      setRecusa(FALTA_NOME)
      return
    }
    aoEnviar({ nome: nome.trim(), cor })
  }

  return (
    <form className="funis__formulario" onSubmit={enviar} noValidate>
      <div className="funis__par">
        <CampoDeNome
          id={id}
          rotulo="Nome da etapa"
          valor={nome}
          recusadoPor={recusa === null ? null : idDaRecusa}
          aoTrocar={(valor) => {
            setRecusa(null)
            setNome(valor)
          }}
        />
        <SeletorDeCor id={`${id}-cor`} rotulo="Cor da etapa" valor={cor} aoTrocar={setCor} />
      </div>
      {recusa === null ? null : (
        <p className="funis__erro" id={idDaRecusa} role="alert">
          {recusa}
        </p>
      )}
      <div className="funis__acoes">
        <button
          className="funis__botao"
          type="submit"
          disabled={ocupado}
          aria-label={acao.nomeAcessivel}
        >
          {enviando ? acao.gravando : acao.parado}
        </button>
        <button
          className="funis__botao funis__botao--discreto"
          type="button"
          disabled={ocupado}
          aria-label={cancelar}
          onClick={aoCancelar}
        >
          Cancelar
        </button>
      </div>
    </form>
  )
}

/** Uma linha do Funil novo: o nome e a cor de uma das Etapas dele. */
interface EtapaDoRascunho {
  chave: number
  nome: string
  cor: CorPastel | null
}

/**
 * O Funil novo **com as Etapas dele, de uma vez** (11.4): o nome e uma lista de linhas (nome e
 * cor de cada Etapa), na ordem do quadro. Um `POST` só — o serviço cria tudo numa transação, e
 * recusa com `422` a lista vazia ou com nome repetido (a mensagem sai como veio).
 */
function FormularioDoNovoFunil({
  ocupado,
  enviando,
  aoEnviar,
}: {
  ocupado: boolean
  enviando: boolean
  aoEnviar: (novo: { nome: string; etapas: { nome: string; cor: CorPastel | null }[] }) => void
}) {
  const [nome, setNome] = useState('')
  const [etapas, setEtapas] = useState<EtapaDoRascunho[]>([{ chave: 0, nome: '', cor: null }])
  const [proximaChave, setProximaChave] = useState(1)
  const [recusa, setRecusa] = useState<string | null>(null)

  function trocarEtapa(chave: number, mudanca: Partial<EtapaDoRascunho>) {
    setRecusa(null)
    setEtapas((atuais) =>
      atuais.map((etapa) => (etapa.chave === chave ? { ...etapa, ...mudanca } : etapa)),
    )
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (nome.trim() === '') {
      setRecusa(FALTA_NOME)
      return
    }
    if (etapas.some((etapa) => etapa.nome.trim() === '')) {
      setRecusa(FALTA_NOME_DE_ETAPA)
      return
    }
    /* O nome repetido é recusado aqui, antes da rede — o serviço diz o mesmo (`422`), mas a pessoa
       não precisa gastar uma ida para saber o que a tela já sabe. A comparação é pelo nome aparado,
       que é o que o serviço grava. */
    const nomes = etapas.map((etapa) => etapa.nome.trim())
    if (new Set(nomes).size !== nomes.length) {
      setRecusa(ETAPAS_REPETIDAS)
      return
    }
    aoEnviar({
      nome: nome.trim(),
      etapas: etapas.map((etapa) => ({ nome: etapa.nome.trim(), cor: etapa.cor })),
    })
  }

  return (
    <form className="funis__formulario" onSubmit={enviar} noValidate>
      <CampoDeNome
        id={ID_DO_CAMPO_DO_NOVO_FUNIL}
        rotulo="Nome do funil"
        valor={nome}
        aoTrocar={(valor) => {
          setRecusa(null)
          setNome(valor)
        }}
      />
      <ol className="funis__etapas-novas" aria-label="Etapas do funil novo">
        {etapas.map((etapa, indice) => {
          const posicao = String(indice + 1)
          return (
            <li className="funis__etapa-nova" key={etapa.chave}>
              <CampoDeNome
                id={`etapa-do-funil-novo-${String(etapa.chave)}`}
                rotulo={`Etapa ${posicao}`}
                valor={etapa.nome}
                aoTrocar={(valor) => {
                  trocarEtapa(etapa.chave, { nome: valor })
                }}
              />
              <SeletorDeCor
                id={`etapa-do-funil-novo-${String(etapa.chave)}-cor`}
                rotulo={`Cor da etapa ${posicao}`}
                valor={etapa.cor}
                aoTrocar={(cor) => {
                  trocarEtapa(etapa.chave, { cor })
                }}
              />
              <button
                className="funis__botao funis__botao--discreto"
                type="button"
                disabled={ocupado || etapas.length === 1}
                aria-label={`Remover a etapa ${posicao}`}
                onClick={() => {
                  setEtapas((atuais) => atuais.filter((uma) => uma.chave !== etapa.chave))
                }}
              >
                Remover
              </button>
            </li>
          )
        })}
      </ol>
      {recusa === null ? null : (
        <p className="funis__erro" role="alert">
          {recusa}
        </p>
      )}
      <div className="funis__acoes">
        <button
          className="funis__botao funis__botao--discreto"
          type="button"
          disabled={ocupado || etapas.length >= MAXIMO_DE_ETAPAS_NO_FUNIL_NOVO}
          onClick={() => {
            setEtapas((atuais) => [...atuais, { chave: proximaChave, nome: '', cor: null }])
            setProximaChave((chave) => chave + 1)
          }}
        >
          + Etapa
        </button>
        <button className="funis__botao" type="submit" disabled={ocupado} aria-label="Criar funil">
          {enviando ? 'Criando…' : 'Criar funil'}
        </button>
      </div>
    </form>
  )
}

/** Uma linha de Etapa: nome, cor, contagem, e as ações — editar, subir, descer, excluir. */
function LinhaDaEtapa({
  etapa,
  funil,
  posicao,
  primeira,
  ultima,
  emEdicao,
  edicaoAberta,
  formularioAberto,
  aConfirmar,
  ocupado,
  salvando,
  aoEditar,
  aoCancelarEdicao,
  aoSalvar,
  aoMover,
  aoPedirExclusao,
  aoCancelarExclusao,
  aoExcluir,
}: {
  etapa: Etapa
  funil: Funil
  /** A posição da etapa no funil, a partir de zero — vira o "1." da linha (protótipo v2). */
  posicao: number
  primeira: boolean
  ultima: boolean
  emEdicao: boolean
  /** Alguma linha da tela está aberta em edição — não necessariamente esta. */
  edicaoAberta: boolean
  /**
   * Algum formulário da tela está aberto: uma edição **ou** uma "Nova etapa" com rascunho.
   *
   * É mais largo que `edicaoAberta` de propósito, e só governa quem **abre** formulário. As
   * demais ações continuam presas à edição: mover ou excluir com um rascunho de etapa nova
   * aberto não desfaz nada de ninguém.
   */
  formularioAberto: boolean
  aConfirmar: boolean
  ocupado: boolean
  /** A **edição desta linha** está em voo — e não qualquer escrita da tela. */
  salvando: boolean
  aoEditar: () => void
  aoCancelarEdicao: () => void
  aoSalvar: (etapa: { nome: string; cor: CorPastel | null }) => void
  aoMover: (deslocamento: -1 | 1) => void
  aoPedirExclusao: () => void
  aoCancelarExclusao: () => void
  aoExcluir: () => void
}) {
  if (emEdicao) {
    return (
      <li className="funis__item">
        <FormularioDaEtapa
          id={`etapa-nome-${etapa.id}`}
          inicial={{ nome: etapa.nome, cor: etapa.cor }}
          acao={{
            parado: 'Salvar etapa',
            gravando: 'Salvando…',
            nomeAcessivel: `Salvar a etapa ${etapa.nome}`,
          }}
          cancelar={`Cancelar a edição de ${etapa.nome}`}
          ocupado={ocupado}
          enviando={salvando}
          aoEnviar={aoSalvar}
          aoCancelar={aoCancelarEdicao}
        />
      </li>
    )
  }

  return (
    <li className="funis__item">
      <div className="funis__linha">
        <span className="funis__numero" aria-hidden="true">{`${String(posicao + 1)}.`}</span>
        <span className="funis__amostra" data-cor={etapa.cor ?? 'nenhuma'} aria-hidden="true" />
        <span className="funis__nome-da-etapa">{etapa.nome}</span>
        <span className="funis__nome-da-cor">{nomeDaCor(etapa.cor)}</span>
        <span className="funis__status">{contagem(etapa.quantidade_de_negocios)}</span>
        <div className="funis__acoes">
          {/*
           * Subir e descer, e não arrastar: o gesto existe no teclado e no leitor de tela, e
           * não custa uma biblioteca. Nas pontas o botão fica desabilitado — mover a primeira
           * para cima não é um erro a explicar, é um movimento que não existe.
           */}
          <button
            className="funis__botao funis__botao--discreto"
            type="button"
            disabled={ocupado || edicaoAberta || primeira}
            aria-label={`Subir ${etapa.nome}`}
            onClick={() => {
              aoMover(-1)
            }}
          >
            ↑
          </button>
          <button
            className="funis__botao funis__botao--discreto"
            type="button"
            disabled={ocupado || edicaoAberta || ultima}
            aria-label={`Descer ${etapa.nome}`}
            onClick={() => {
              aoMover(1)
            }}
          >
            ↓
          </button>
          {/*
           * "Renomear" fica desabilitado enquanto algum formulário está aberto: abrir o
           * segundo não fecha o primeiro, e o primeiro continua montado com o rascunho
           * digitado no estado. Reabrindo-o, o texto abandonado voltaria — exatamente o que
           * "Cancelar" existe para impedir. Um formulário por vez, e o de "Nova etapa" conta:
           * ele também guarda rascunho.
           */}
          <button
            className="funis__botao funis__botao--discreto"
            type="button"
            disabled={ocupado || formularioAberto}
            aria-label={`Editar ${etapa.nome}`}
            onClick={aoEditar}
          >
            Editar
          </button>
          <AcaoDeExcluir
            id={`etapa-${etapa.id}`}
            alvo={`a etapa ${etapa.nome} do funil ${funil.nome}`}
            razaoDoBloqueio={BLOQUEIO_DA_ETAPA}
            quantidadeDeNegocios={etapa.quantidade_de_negocios}
            aConfirmar={aConfirmar}
            ocupado={ocupado || edicaoAberta}
            aoPedir={aoPedirExclusao}
            aoCancelar={aoCancelarExclusao}
            aoExcluir={aoExcluir}
          />
        </div>
      </div>
    </li>
  )
}

export function FunisEEtapas() {
  const ehCelular = useEhCelular()
  const consulta = useFunis()
  const criacaoDeFunil = useCriarFunil()
  const renomeacaoDeFunil = useRenomearFunil()
  const exclusaoDeFunil = useExcluirFunil()
  const criacaoDeEtapa = useCriarEtapa()
  const edicaoDeEtapa = useAtualizarEtapa()
  const exclusaoDeEtapa = useExcluirEtapa()
  const reordenacao = useReordenarEtapas()

  /** O `id` da linha aberta em edição — Funil ou Etapa, uma por vez na tela inteira. */
  const [emEdicao, setEmEdicao] = useState<string | null>(null)
  /** O `id` do que pediu exclusão e espera a confirmação. */
  const [aConfirmar, setAConfirmar] = useState<string | null>(null)
  /** O `id` do Funil cujo formulário de "Nova etapa" está aberto. */
  const [comEtapaNova, setComEtapaNova] = useState<string | null>(null)
  /**
   * Há rascunho em algum formulário da tela — de edição ou de etapa nova.
   *
   * É ela que governa quem **abre** formulário, e não `emEdicao` sozinha: "+ Nova etapa" em
   * outro Funil trocaria `comEtapaNova` de dono e jogaria fora, calado, o nome já digitado.
   */
  const formularioAberto = emEdicao !== null || comEtapaNova !== null
  const [erro, setErro] = useState<string | null>(null)
  const [confirmacao, setConfirmacao] = useState<string | null>(null)
  /**
   * Quantos funis esta montagem já criou — é a `key` do formulário de "Novo funil".
   *
   * O rascunho mora **dentro** do formulário (é ele quem valida o campo vazio), e por isso
   * limpá-lo daqui é remontá-lo. Sem isto, o nome recém-criado continuaria no campo e o clique
   * seguinte mandaria o mesmo nome de novo — que o serviço recusa com `409`, mandando quem
   * usa procurar o erro num campo que ele não digitou.
   */
  const [funisCriados, setFunisCriados] = useState(0)

  const ocupado =
    criacaoDeFunil.isPending ||
    renomeacaoDeFunil.isPending ||
    exclusaoDeFunil.isPending ||
    criacaoDeEtapa.isPending ||
    edicaoDeEtapa.isPending ||
    exclusaoDeEtapa.isPending ||
    reordenacao.isPending

  const funis = consulta.data

  /**
   * Os avisos falam do envio que está em voo — nunca do anterior.
   *
   * Sem isto, a recusa de uma gravação continua na tela enquanto a próxima corre, e como o
   * `role="status"` da confirmação só aparece com `erro === null`, o sucesso seguinte ficaria
   * mudo até que a mudança de estado o limpasse.
   */
  function limparOsAvisos() {
    setErro(null)
    setConfirmacao(null)
  }

  function aoFalhar(falha: unknown) {
    setConfirmacao(null)
    setErro(mensagemDe(falha))
  }

  /** O desfecho comum das sete mutações: confirma, fecha o que estava aberto, ou explica. */
  function aoTerminar(mensagem: string, fechar?: () => void) {
    return {
      onSuccess: () => {
        setErro(null)
        setConfirmacao(mensagem)
        fechar?.()
      },
      onError: aoFalhar,
    }
  }

  function criarOFunil(novo: { nome: string; etapas: { nome: string; cor: CorPastel | null }[] }) {
    limparOsAvisos()
    criacaoDeFunil.mutate(
      novo,
      aoTerminar(FUNIL_CRIADO, () => {
        setFunisCriados((quantos) => quantos + 1)
      }),
    )
  }

  function renomearOFunil(funilId: string, nome: string) {
    limparOsAvisos()
    renomeacaoDeFunil.mutate(
      { funilId, nome },
      aoTerminar(FUNIL_SALVO, () => {
        setEmEdicao(null)
      }),
    )
  }

  function excluirOFunil(funilId: string) {
    limparOsAvisos()
    exclusaoDeFunil.mutate(
      funilId,
      aoTerminar(FUNIL_EXCLUIDO, () => {
        setAConfirmar(null)
      }),
    )
  }

  function criarAEtapa(funilId: string, etapa: { nome: string; cor: CorPastel | null }) {
    limparOsAvisos()
    criacaoDeEtapa.mutate(
      { funilId, ...etapa },
      aoTerminar(ETAPA_CRIADA, () => {
        setComEtapaNova(null)
      }),
    )
  }

  function editarAEtapa(
    funilId: string,
    etapaId: string,
    etapa: { nome: string; cor: CorPastel | null },
  ) {
    limparOsAvisos()
    edicaoDeEtapa.mutate(
      { funilId, etapaId, ...etapa },
      aoTerminar(ETAPA_SALVA, () => {
        setEmEdicao(null)
      }),
    )
  }

  function excluirAEtapa(funilId: string, etapaId: string) {
    limparOsAvisos()
    exclusaoDeEtapa.mutate(
      { funilId, etapaId },
      aoTerminar(ETAPA_EXCLUIDA, () => {
        setAConfirmar(null)
      }),
    )
  }

  /**
   * Troca a Etapa com a vizinha e manda a **lista completa** ao serviço.
   *
   * A troca é feita aqui, sobre a ordem que a tela está exibindo, e o que viaja é a lista
   * inteira — que é o que a rota aceita. Um "trocar duas" no serviço precisaria de constraint
   * adiável no banco e abriria um estado intermediário inválido.
   */
  function moverAEtapa(funil: Funil, indice: number, deslocamento: -1 | 1) {
    const destino = indice + deslocamento
    if (destino < 0 || destino >= funil.etapas.length) {
      return
    }
    const ordem = funil.etapas.map((etapa) => etapa.id)
    // A troca é feita por índices que o `if` acima já provou existirem; o `?? ''` está aqui
    // porque o compilador lê `array[i]` como possivelmente ausente (`noUncheckedIndexedAccess`),
    // e não porque a ausência seja alcançável.
    const daqui = ordem[indice] ?? ''
    const dali = ordem[destino] ?? ''
    ordem[indice] = dali
    ordem[destino] = daqui
    limparOsAvisos()
    reordenacao.mutate({ funilId: funil.id, etapas: ordem }, aoTerminar(ORDEM_SALVA))
  }

  return (
    <section className="funis" aria-labelledby={ID_DO_TITULO}>
      <div className="funis__cartao">
        {/* O nome da região continua para o leitor de tela; na tela não há título nem caixa
            (protótipo v2): os cartões de funil são o corpo da tela. */}
        <h2 className="funis__titulo funis__so-para-leitor" id={ID_DO_TITULO}>
          Funis e etapas
        </h2>

        <div className="funis__corpo">
          {consulta.isError ? (
            /* Verbatim: a recusa do serviço é a que sabe explicar o caso. */
            <p className="funis__erro" role="alert">
              {mensagemDe(consulta.error)}
            </p>
          ) : /* `|| funis === undefined` é o que fecha o terceiro estado: sem rede, a consulta
                fica **pausada** — nem erro, nem `isLoading` —, e sem esta metade a tela diria
                "nenhum funil cadastrado", afirmando ausência onde só há desconhecimento. */
          consulta.isLoading || funis === undefined ? (
            <p className="funis__ajuda" role="status">
              {CARREGANDO}
            </p>
          ) : funis.length === 0 ? (
            <p className="funis__ajuda">{SEM_FUNIS}</p>
          ) : (
            /* A grade: dois no computador, empilhados no celular. A decisão é do
               `useEhCelular` do shell, e não de uma `@media` — é o mesmo mecanismo que decide
               a barra inferior, e ter duas fontes de verdade de "isto é um celular" faria a
               tela e o shell discordarem numa largura de fronteira. */
            <div className={`funis__grade${ehCelular ? ' funis__grade--empilhada' : ''}`}>
              {funis.map((funil) => (
                <article className="funis__funil" key={`${funil.id}-${funil.nome}`}>
                  {emEdicao === funil.id ? (
                    <FormularioDeNome
                      id={`funil-nome-${funil.id}`}
                      rotulo="Nome do funil"
                      inicial={funil.nome}
                      acao={{
                        parado: 'Salvar funil',
                        gravando: 'Salvando…',
                        nomeAcessivel: `Salvar o funil ${funil.nome}`,
                      }}
                      cancelar={`Cancelar a edição de ${funil.nome}`}
                      ocupado={ocupado}
                      enviando={renomeacaoDeFunil.isPending}
                      aoEnviar={(nome) => {
                        renomearOFunil(funil.id, nome)
                      }}
                      aoCancelar={() => {
                        limparOsAvisos()
                        setEmEdicao(null)
                      }}
                    />
                  ) : (
                    <div className="funis__linha funis__linha--funil">
                      <h3 className="funis__nome-do-funil">{funil.nome}</h3>
                      {/* O significado do tipo, e não o valor cru nem o nome dele: quem
                          administra precisa saber o que o Funil faz com a visibilidade dos
                          Negócios dele, e "prospeccao" não diz isso. A classe por tipo é o que
                          nomeia o tipo no HTML sem depender do texto exibido. */}
                      <span className={`funis__tipo funis__tipo--${funil.tipo}`}>
                        {explicacaoDoTipo(funil.tipo)}
                      </span>
                      <span className="funis__status">
                        {contagem(funil.quantidade_de_negocios)}
                      </span>
                      <div className="funis__acoes">
                        <button
                          className="funis__botao funis__botao--discreto"
                          type="button"
                          disabled={ocupado || formularioAberto}
                          aria-label={`Renomear o funil ${funil.nome}`}
                          onClick={() => {
                            limparOsAvisos()
                            setAConfirmar(null)
                            setEmEdicao(funil.id)
                          }}
                        >
                          Renomear
                        </button>
                        <AcaoDeExcluir
                          id={`funil-${funil.id}`}
                          alvo={`o funil ${funil.nome}`}
                          razaoDoBloqueio={BLOQUEIO_DO_FUNIL}
                          quantidadeDeNegocios={funil.quantidade_de_negocios}
                          aConfirmar={aConfirmar === funil.id}
                          ocupado={ocupado || emEdicao !== null}
                          aoPedir={() => {
                            limparOsAvisos()
                            setAConfirmar(funil.id)
                          }}
                          aoCancelar={() => {
                            limparOsAvisos()
                            setAConfirmar(null)
                          }}
                          aoExcluir={() => {
                            excluirOFunil(funil.id)
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {funil.etapas.length === 0 ? (
                    <p className="funis__ajuda">{SEM_ETAPAS}</p>
                  ) : (
                    /* A `<ul>` ganha nome próprio: são várias listas na tela, e sem ele o
                       leitor de tela anuncia "lista" e quem navega por marcos não sabe em qual
                       entrou. */
                    <ul className="funis__etapas" aria-label={`Etapas de ${funil.nome}`}>
                      {funil.etapas.map((etapa, indice) => (
                        <LinhaDaEtapa
                          // `key` com o nome junto: a linha guarda o rascunho da edição no
                          // estado, e sem a remontagem ele atravessaria uma releitura da lista
                          // — reabrir "Renomear" mostraria o texto velho, e salvar desfaria a
                          // alteração de outra pessoa. Mesma regra da 2.3.
                          key={`${etapa.id}-${etapa.nome}`}
                          etapa={etapa}
                          funil={funil}
                          posicao={indice}
                          primeira={indice === 0}
                          ultima={indice === funil.etapas.length - 1}
                          emEdicao={emEdicao === etapa.id}
                          edicaoAberta={emEdicao !== null}
                          formularioAberto={formularioAberto}
                          aConfirmar={aConfirmar === etapa.id}
                          ocupado={ocupado}
                          salvando={edicaoDeEtapa.isPending && emEdicao === etapa.id}
                          aoEditar={() => {
                            limparOsAvisos()
                            setAConfirmar(null)
                            setEmEdicao(etapa.id)
                          }}
                          aoCancelarEdicao={() => {
                            // Cancelar também limpa: uma recusa do servidor continuaria visível
                            // sem nada pendente na tela que a explicasse — e a confirmação
                            // seguinte ficaria muda, porque ela só aparece com `erro === null`.
                            limparOsAvisos()
                            setEmEdicao(null)
                          }}
                          aoSalvar={(nova) => {
                            editarAEtapa(funil.id, etapa.id, nova)
                          }}
                          aoMover={(deslocamento) => {
                            moverAEtapa(funil, indice, deslocamento)
                          }}
                          aoPedirExclusao={() => {
                            limparOsAvisos()
                            setAConfirmar(etapa.id)
                          }}
                          aoCancelarExclusao={() => {
                            limparOsAvisos()
                            setAConfirmar(null)
                          }}
                          aoExcluir={() => {
                            excluirAEtapa(funil.id, etapa.id)
                          }}
                        />
                      ))}
                    </ul>
                  )}

                  {comEtapaNova === funil.id ? (
                    <FormularioDaEtapa
                      id={`etapa-nova-${funil.id}`}
                      inicial={{ nome: '', cor: null }}
                      acao={{
                        parado: 'Criar etapa',
                        gravando: 'Criando…',
                        nomeAcessivel: `Criar etapa em ${funil.nome}`,
                      }}
                      cancelar={`Cancelar a nova etapa de ${funil.nome}`}
                      ocupado={ocupado}
                      enviando={criacaoDeEtapa.isPending}
                      aoEnviar={(nova) => {
                        criarAEtapa(funil.id, nova)
                      }}
                      aoCancelar={() => {
                        limparOsAvisos()
                        setComEtapaNova(null)
                      }}
                    />
                  ) : (
                    <div className="funis__acoes">
                      <button
                        className="funis__botao funis__botao--discreto"
                        type="button"
                        disabled={ocupado || formularioAberto}
                        aria-label={`Nova etapa em ${funil.nome}`}
                        onClick={() => {
                          limparOsAvisos()
                          setComEtapaNova(funil.id)
                        }}
                      >
                        + Nova etapa
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          )}

          {consulta.isError ? null : (
            <p className="funis__dica">
              Uma etapa com negócios não pode ser excluída — mova todos antes. Nada é movido
              automaticamente. A cor da etapa aparece no topo da coluna e na barra do negócio.
            </p>
          )}
          {consulta.isError ? null : (
            <div className="funis__novo">
              <h3 className="funis__subtitulo">Novo funil</h3>
              <FormularioDoNovoFunil
                key={funisCriados}
                ocupado={ocupado}
                enviando={criacaoDeFunil.isPending}
                aoEnviar={criarOFunil}
              />
            </div>
          )}

          {erro === null ? null : (
            <p className="funis__erro" role="alert">
              {erro}
            </p>
          )}
          {/* `role="status"` e não `role="alert"`: deu certo, e o leitor de tela não precisa
              interromper o que a pessoa estiver fazendo para dizer isso. */}
          {confirmacao === null || erro !== null ? null : (
            <p className="funis__sucesso" role="status">
              {confirmacao}
            </p>
          )}
        </div>
      </div>
    </section>
  )
}
